// src/admin/pages/MainsManagement.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  PenTool,
  Award,
  Edit2,
  Trash2,
  Upload,
  Check,
  BookOpen,
  Folder,
  FolderOpen,
  Calendar,
  ArrowLeft,
  Mail,
  Phone,
  User,
} from "lucide-react";
import { useAdminData } from "../context/AdminDataContext";
import Modal from "../components/Modal";
import StudentProfileDrawer from "../components/StudentProfileDrawer";
import { mainsStructure } from "../data/initialQuestionBankData";

const gsPapers = [
  "GS Paper I (Heritage, History, Geo, Society)",
  "GS Paper II (Governance, Constitution, Polity, IR)",
  "GS Paper III (Technology, Bio, Economy, Security)",
  "GS Paper IV (Ethics, Integrity & Aptitude)",
];

const mainsSubjectFolders = [
  "Indian Polity",
  "Governance",
  "International Relations",
  "History",
  "Geography",
  "Indian Society",
  "Indian Economy",
  "Environment",
  "Science & Technology",
  "Internal Security",
  "Ethics & Integrity",
  "Essay Writing",
];

const MainsManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    mainsInitiatives,
    addMainsInitiative,
    updateMainsInitiative,
    deleteMainsInitiative,
    togglePublishMainsInitiative,
    evaluateMainsSubmission,
    studentResults,
    mainsQuestionBank,
  } = useAdminData();

  // Active Tab: 'initiatives' | 'evaluations'
  const [activeTab, setActiveTab] = useState("initiatives");

  // Search and filter states
  const [initiativeSearch, setInitiativeSearch] = useState("");
  const [filterPaper, setFilterPaper] = useState("All");
  const [filterFolder, setFilterFolder] = useState("All");
  const [filterDate, setFilterDate] = useState("");
  const [evalSearch, setEvalSearch] = useState("");
  const [selectedInitiativeForSubmissions, setSelectedInitiativeForSubmissions] = useState(null);
  const [viewingProfileStudent, setViewingProfileStudent] = useState(null);
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState("All");
  const [submissionDateFilter, setSubmissionDateFilter] = useState("");

  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingInitiative, setEditingInitiative] = useState(null);
  const [evaluatingResult, setEvaluatingResult] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  // Question Bank Picker Modal State (Folder-Based)
  const [isQbPickerOpen, setIsQbPickerOpen] = useState(false);
  const [qbActiveFolder, setQbActiveFolder] = useState(null); // { gsPaper, subject }
  const [qbSearch, setQbSearch] = useState("");

  // Form State for Mains Initiative
  const [initiativeForm, setInitiativeForm] = useState({
    title: "",
    folder: "Indian Polity",
    gsPaper: "GS Paper II (Governance, Constitution, Polity, IR)",
    subject: "Polity & Governance",
    topic: "Mains Practice",
    question: "",
    instructions: "Answer in 250 words. Focus on core dimensions, contemporary examples, and constructive way forward.",
    maxMarks: 15,
    wordLimit: 250,
    deadline: new Date().toISOString().split("T")[0],
    status: "Published",
    referencePdfUrl: "",
    referencePdfName: "",
  });

  // Evaluation Form State
  const [evalData, setEvalData] = useState({
    marksObtained: 9,
    feedback: "",
    evaluationStatus: "Evaluated",
  });

  // URL trigger ?action=new
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openAddModal();
      searchParams.delete("action");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const openAddModal = () => {
    setEditingInitiative(null);
    setPdfFile(null);
    const today = new Date().toISOString().split("T")[0];
    setInitiativeForm({
      title: "",
      folder: "Indian Polity",
      date: today,
      gsPaper: gsPapers[1],
      subject: "Polity & Governance",
      topic: "Mains Practice",
      question: "",
      instructions: "Answer in 250 words. Focus on core dimensions, contemporary examples, and constructive way forward.",
      maxMarks: 15,
      wordLimit: 250,
      deadline: today,
      status: "Published",
      referencePdfUrl: "",
      referencePdfName: "",
    });
    setIsFormModalOpen(true);
  };

  const openEditModal = (init) => {
    setEditingInitiative(init);
    setPdfFile(null);
    const initDate = init.date || init.deadline || new Date().toISOString().split("T")[0];
    setInitiativeForm({
      title: init.title || "",
      folder: init.folder || init.subject || "Indian Polity",
      date: initDate,
      gsPaper: init.gsPaper || gsPapers[1],
      subject: init.subject || "General Studies",
      topic: init.topic || "Mains Practice",
      question: init.question || "",
      instructions: init.instructions || "Answer in 250 words.",
      maxMarks: init.maxMarks || 15,
      wordLimit: init.wordLimit || 250,
      deadline: init.deadline || initDate,
      status: init.status || "Published",
      referencePdfUrl: init.referencePdfUrl || "",
      referencePdfName: init.referencePdfName || "",
    });
    setIsFormModalOpen(true);
  };

  const handleSaveInitiative = (e, forceStatus) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!initiativeForm.title.trim()) {
      alert("Please provide an initiative title.");
      return;
    }
    if (!initiativeForm.question.trim()) {
      alert("Please enter the Mains question prompt.");
      return;
    }

    const payload = {
      ...initiativeForm,
      status: forceStatus || initiativeForm.status || "Published",
    };

    if (editingInitiative) {
      updateMainsInitiative(editingInitiative.id, payload, pdfFile);
    } else {
      addMainsInitiative(payload, pdfFile);
    }
    setIsFormModalOpen(false);
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPdfFile(file);
      setInitiativeForm((prev) => ({
        ...prev,
        referencePdfName: file.name,
        referencePdfUrl: URL.createObjectURL(file),
      }));
    }
  };

  const handleSelectFromQb = (qbItem) => {
    const matchedPaper =
      gsPapers.find((p) =>
        p.toLowerCase().includes((qbItem.gsPaper || "").toLowerCase())
      ) || initiativeForm.gsPaper;

    setInitiativeForm((prev) => ({
      ...prev,
      title: prev.title || `${qbItem.gsPaper || "GS Paper"}: ${qbItem.topic || "Mains Practice"}`,
      question: qbItem.questionText || prev.question,
      gsPaper: matchedPaper,
      subject: qbItem.subject || prev.subject,
      topic: qbItem.topic || prev.topic,
      maxMarks: qbItem.maxMarks || prev.maxMarks,
      wordLimit: qbItem.wordLimit || prev.wordLimit,
      instructions:
        qbItem.referenceAnswer
          ? `Reference Structure / Model Points:\n${qbItem.referenceAnswer}`
          : prev.instructions ||
            "Answer according to the specified word limit. Sub-divide into Introduction, Arguments/Body, and Way Forward.",
    }));
    setIsQbPickerOpen(false);
  };

  // Open evaluation modal for a student result
  const openEvaluationModal = (res) => {
    setEvaluatingResult(res);
    setEvalData({
      marksObtained: res.marksObtained || 9,
      feedback:
        res.feedback ||
        "Structure is sound. Good use of Article references. Needs stronger contemporary examples of bills passed without referral in the last 2 sessions. Conclusion offers clear forward-looking reforms.",
      evaluationStatus: "Evaluated",
    });
  };

  const handleSaveEvaluation = (e) => {
    e.preventDefault();
    if (evalData.marksObtained < 0 || evalData.marksObtained > (evaluatingResult.totalMarks || 15)) {
      alert(`Marks must be between 0 and ${evaluatingResult.totalMarks || 15}`);
      return;
    }

    evaluateMainsSubmission(evaluatingResult.id, evalData);
    setEvaluatingResult(null);
  };

  // Filter Initiatives by Search, GS Paper, Folder, and Upload/Test Date
  const filteredInitiatives = (mainsInitiatives || []).filter((init) => {
    const matchesSearch =
      (init?.title || "").toLowerCase().includes((initiativeSearch || "").toLowerCase()) ||
      (init?.question || "").toLowerCase().includes((initiativeSearch || "").toLowerCase());
    const matchesPaper =
      filterPaper === "All" || (init?.gsPaper && init.gsPaper.includes(filterPaper));
    const matchesFolder =
      filterFolder === "All" ||
      (init?.folder && init.folder.toLowerCase() === filterFolder.toLowerCase()) ||
      (init?.subject && init.subject.toLowerCase() === filterFolder.toLowerCase());
    const initDate = init?.date || init?.deadline || "";
    const matchesDate = !filterDate || initDate.startsWith(filterDate);
    return matchesSearch && matchesPaper && matchesFolder && matchesDate;
  });

  // Filter Mains Student Submissions
  const mainsSubmissions = (studentResults || []).filter((r) => {
    if (!r || r.examType !== "Mains") return false;

    // Filter by selected initiative if active
    if (selectedInitiativeForSubmissions) {
      const targetInitId = String(
        selectedInitiativeForSubmissions.id || selectedInitiativeForSubmissions._id || ""
      );
      const rowInitId = String(r.initiativeId || "");
      const targetTitle = (selectedInitiativeForSubmissions.title || "").trim().toLowerCase();
      const rowTitle = (r.initiativeName || "").trim().toLowerCase();

      const matchesId = targetInitId && rowInitId && targetInitId === rowInitId;
      const matchesTitle =
        targetTitle &&
        rowTitle &&
        (targetTitle === rowTitle || targetTitle.includes(rowTitle) || rowTitle.includes(targetTitle));

      if (!matchesId && !matchesTitle) return false;
    }

    // Status filter
    if (submissionStatusFilter !== "All") {
      const isEval =
        r.resultStatus === "Evaluated" ||
        r.evaluationStatus === "Evaluated" ||
        r.evaluationStatus === "evaluated";
      if (submissionStatusFilter === "Evaluated" && !isEval) return false;
      if (submissionStatusFilter === "Pending Review" && isEval) return false;
    }

    // Date filter
    if (submissionDateFilter) {
      if (!r.submissionDate || !r.submissionDate.startsWith(submissionDateFilter)) return false;
    }

    // Search query
    if (evalSearch.trim()) {
      const q = evalSearch.toLowerCase();
      const matchesName = (r.studentName || "").toLowerCase().includes(q);
      const matchesEmail = (r.email || r.studentEmail || "").toLowerCase().includes(q);
      const matchesPhone = (r.phone || r.mobileNumber || "").toLowerCase().includes(q);
      const matchesReg = (r.regId || "").toLowerCase().includes(q);
      const matchesPaper = (r.gsPaper || "").toLowerCase().includes(q);
      const matchesInit = (r.initiativeName || "").toLowerCase().includes(q);
      const matchesQuestion = (r.questionText || "").toLowerCase().includes(q);
      if (
        !matchesName &&
        !matchesEmail &&
        !matchesPhone &&
        !matchesReg &&
        !matchesPaper &&
        !matchesInit &&
        !matchesQuestion
      ) {
        return false;
      }
    }

    return true;
  });

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Mains Initiative Management
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Publish daily UPSC Mains answer-writing prompts and evaluate student submissions with expert faculty feedback.
          </p>
        </div>

        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-900 hover:bg-red-800 text-white font-bold text-xs shadow-lg shadow-red-900/20 transition-all transform active:scale-95"
        >
          <Plus size={16} /> Create Mains Initiative
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-4 text-xs font-bold">
        <button
          onClick={() => setActiveTab("initiatives")}
          className={`pb-3 px-2 border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "initiatives"
              ? "border-red-900 text-red-900"
              : "border-transparent text-slate-400 hover:text-slate-700"
          }`}
        >
          <BookOpen size={16} />
          <span>Mains Initiatives ({mainsInitiatives.length})</span>
        </button>
        <button
          onClick={() => setActiveTab("evaluations")}
          className={`pb-3 px-2 border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "evaluations"
              ? "border-red-900 text-red-900"
              : "border-transparent text-slate-400 hover:text-slate-700"
          }`}
        >
          <PenTool size={16} />
          <span>Student Submissions & Evaluation ({mainsSubmissions.length})</span>
        </button>
      </div>

      {/* TAB 1: Mains Initiatives */}
      {activeTab === "initiatives" && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="relative flex-1 w-full max-w-md">
              <Search
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={initiativeSearch}
                onChange={(e) => setInitiativeSearch(e.target.value)}
                placeholder="Search Mains prompts by title or question..."
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
              {/* Folder Filter */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                  <Folder size={13} className="text-red-700" /> Folder:
                </span>
                <select
                  value={filterFolder}
                  onChange={(e) => setFilterFolder(e.target.value)}
                  className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20"
                >
                  <option value="All">All Folders</option>
                  {mainsSubjectFolders.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              {/* GS Paper Filter */}
              <select
                value={filterPaper}
                onChange={(e) => setFilterPaper(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none"
              >
                <option value="All">All GS Papers</option>
                <option value="GS Paper I">GS Paper I</option>
                <option value="GS Paper II">GS Paper II</option>
                <option value="GS Paper III">GS Paper III</option>
                <option value="GS Paper IV">GS Paper IV</option>
              </select>

              {/* Upload / Test Date Filter */}
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20"
                title="Filter by Upload / Test Date"
              />

              {(initiativeSearch || filterFolder !== "All" || filterPaper !== "All" || filterDate) && (
                <button
                  onClick={() => {
                    setInitiativeSearch("");
                    setFilterFolder("All");
                    setFilterPaper("All");
                    setFilterDate("");
                  }}
                  className="text-xs text-rose-600 font-bold hover:underline px-1.5"
                >
                  Clear
                </button>
              )}

              <span className="text-xs text-slate-500 font-medium whitespace-nowrap pl-2">
                Showing {filteredInitiatives.length} initiatives
              </span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                    <th className="py-3.5 px-4">Title & Question</th>
                    <th className="py-3.5 px-3">Folder</th>
                    <th className="py-3.5 px-3">GS Paper</th>
                    <th className="py-3.5 px-3">Upload / Test Date</th>
                    <th className="py-3.5 px-3 text-center">Marks</th>
                    <th className="py-3.5 px-3 text-center">Words</th>
                    <th className="py-3.5 px-3 text-center">Submissions</th>
                    <th className="py-3.5 px-3 text-center">Status</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredInitiatives.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-slate-400">
                        No Mains initiatives match the selected search, folder, paper, or date filters.
                      </td>
                    </tr>
                  ) : (
                    filteredInitiatives.map((init) => {
                      const isPublished = init.status === "Published";
                      const folderName = init.folder || init.subject || "Indian Polity";
                      const initDateStr = init.date || init.deadline
                        ? new Date(init.date || init.deadline).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "Today";

                      return (
                        <tr
                          key={init.id}
                          className="hover:bg-slate-50/70 transition-colors group"
                        >
                          <td className="py-4 px-4 max-w-md">
                            <div className="font-bold text-slate-900 group-hover:text-red-900 transition-colors line-clamp-1">
                              {init.title}
                            </div>
                            <div className="text-[11px] text-slate-600 line-clamp-2 mt-0.5 font-serif italic">
                              "{init.question}"
                            </div>
                            <div className="text-[10px] text-slate-400 mt-1">
                              {init.subject} • {init.topic}
                            </div>
                          </td>
                          <td className="py-4 px-3 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-red-50 text-red-950 border border-red-200">
                              <Folder size={11} className="text-red-700" />
                              {folderName}
                            </span>
                          </td>
                          <td className="py-4 px-3 whitespace-nowrap">
                            <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                              {init.gsPaper?.split("(")[0]?.trim() || "GS Paper"}
                            </span>
                          </td>
                          <td className="py-4 px-3 font-mono text-[11px] text-slate-600 whitespace-nowrap">
                            {initDateStr}
                          </td>
                          <td className="py-4 px-3 text-center font-bold text-slate-800">
                            {init.maxMarks || 15}
                          </td>
                          <td className="py-4 px-3 text-center font-mono text-slate-600">
                            {init.wordLimit || 250}w
                          </td>
                          <td className="py-4 px-3 text-center font-bold text-slate-800">
                            {init.submissionsCount || 48}
                          </td>
                          <td className="py-4 px-3 text-center whitespace-nowrap">
                            <button
                              onClick={() => togglePublishMainsInitiative(init.id)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                                isPublished
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}
                            >
                              {init.status}
                            </button>
                          </td>
                          <td className="py-4 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => openEditModal(init)}
                                className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"
                                title="Edit Prompt"
                              >
                                <Edit2 size={15} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(init.id)}
                                className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                                title="Delete Prompt"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Student Submissions Evaluation Table */}
      {activeTab === "evaluations" && (
        <div className="space-y-4">
          {/* Breadcrumb banner when viewing a specific initiative */}
          {selectedInitiativeForSubmissions && (
            <div className="bg-gradient-to-r from-red-950 via-[#450a0a] to-[#0b1528] rounded-2xl p-5 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-md border border-red-800/30">
              <div>
                <button
                  onClick={() => setSelectedInitiativeForSubmissions(null)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-300 hover:text-white mb-1.5 transition-colors"
                >
                  <ArrowLeft size={13} /> Back to All Mains Initiatives
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-white">
                    {selectedInitiativeForSubmissions.title}
                  </h3>
                  <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-white/10 text-red-200 border border-white/15">
                    {selectedInitiativeForSubmissions.gsPaper?.split("(")[0]?.trim() || "GS Paper"} • Max: {selectedInitiativeForSubmissions.maxMarks || 15} marks
                  </span>
                </div>
                <span className="text-xs text-red-200/80 mt-1 block">
                  Showing all students who submitted answers for this initiative ({mainsSubmissions.length} submissions)
                </span>
              </div>
              <button
                onClick={() => setSelectedInitiativeForSubmissions(null)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold self-start sm:self-auto transition-all"
              >
                Show All Submissions
              </button>
            </div>
          )}

          {/* Search and Filters Bar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={evalSearch}
                onChange={(e) => setEvalSearch(e.target.value)}
                placeholder="Search by student name, email, mobile, question..."
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <select
                value={submissionStatusFilter}
                onChange={(e) => setSubmissionStatusFilter(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20"
              >
                <option value="All">All Statuses</option>
                <option value="Evaluated">Evaluated</option>
                <option value="Pending Review">Pending Review</option>
              </select>

              <input
                type="date"
                value={submissionDateFilter}
                onChange={(e) => setSubmissionDateFilter(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20"
                title="Filter by submission date"
              />

              {(evalSearch || submissionStatusFilter !== "All" || submissionDateFilter) && (
                <button
                  onClick={() => {
                    setEvalSearch("");
                    setSubmissionStatusFilter("All");
                    setSubmissionDateFilter("");
                  }}
                  className="px-3 py-2 text-rose-600 hover:bg-rose-50 rounded-xl font-bold transition-colors"
                >
                  Clear
                </button>
              )}

              <span className="text-xs text-slate-500 font-medium pl-2">
                Submissions: <strong>{mainsSubmissions.length}</strong>
              </span>
            </div>
          </div>

          {/* Submissions Clean Table */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                    <th className="py-3.5 px-4">Student Name</th>
                    <th className="py-3.5 px-3">Email Address</th>
                    <th className="py-3.5 px-3">Mobile Number</th>
                    <th className="py-3.5 px-3">GS Paper</th>
                    <th className="py-3.5 px-3">Question</th>
                    <th className="py-3.5 px-3">Submission Date</th>
                    <th className="py-3.5 px-2.5 text-center">Word Count</th>
                    <th className="py-3.5 px-2.5 text-center">Marks</th>
                    <th className="py-3.5 px-2.5 text-center">Max Marks</th>
                    <th className="py-3.5 px-3 text-center">Evaluation Status</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {mainsSubmissions.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="py-12 text-center text-slate-400">
                        No student Mains submissions match the selected filters.
                      </td>
                    </tr>
                  ) : (
                    mainsSubmissions.map((res) => {
                      const isEvaluated =
                        res.resultStatus === "Evaluated" ||
                        res.evaluationStatus === "Evaluated" ||
                        res.evaluationStatus === "evaluated";
                      const maxScore = res.maxMarks || res.totalMarks || 15;
                      const words =
                        res.wordCount ||
                        (res.submittedAnswer ? res.submittedAnswer.trim().split(/\s+/).length : 240);

                      return (
                        <tr
                          key={res.id || res._id}
                          className="hover:bg-slate-50/70 transition-colors group"
                        >
                          {/* Student Name */}
                          <td className="py-3.5 px-4 font-bold text-slate-900 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-red-100 text-red-900 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {(res.studentName || "U")[0]}
                              </div>
                              <div>
                                <div className="group-hover:text-red-900 transition-colors">
                                  {res.studentName}
                                </div>
                                <div className="text-[10px] text-slate-400 font-mono font-normal">
                                  {res.regId || "SIA-2026-9001"}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Email Address */}
                          <td className="py-3.5 px-3 text-slate-600">
                            <a
                              href={`mailto:${res.email || res.studentEmail}`}
                              className="hover:text-red-900 hover:underline flex items-center gap-1 truncate max-w-[170px]"
                              title={res.email || res.studentEmail}
                            >
                              <Mail size={12} className="text-slate-400 flex-shrink-0" />
                              <span className="truncate">{res.email || res.studentEmail || "student@example.com"}</span>
                            </a>
                          </td>

                          {/* Mobile Number */}
                          <td className="py-3.5 px-3 font-mono text-slate-700 whitespace-nowrap">
                            <a
                              href={`tel:${(res.phone || res.mobileNumber || "+919840122334").replace(/\s+/g, "")}`}
                              className="hover:text-red-900 flex items-center gap-1"
                              title="Call student"
                            >
                              <Phone size={12} className="text-slate-400" />
                              <span>{res.phone || res.mobileNumber || "+91 98401 22334"}</span>
                            </a>
                          </td>

                          {/* GS Paper */}
                          <td className="py-3.5 px-3 whitespace-nowrap">
                            <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-800 border border-slate-200">
                              {res.gsPaper?.split("(")[0]?.trim() || "GS Paper"}
                            </span>
                          </td>

                          {/* Question */}
                          <td className="py-3.5 px-3 max-w-xs">
                            <div className="font-semibold text-slate-800 truncate" title={res.initiativeName}>
                              {res.initiativeName}
                            </div>
                            <div className="text-[11px] text-slate-500 font-serif italic truncate mt-0.5" title={res.questionText}>
                              "{res.questionText || "Critically evaluate the constitutional mechanisms..."}"
                            </div>
                          </td>

                          {/* Submission Date */}
                          <td className="py-3.5 px-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                            {res.submissionDate || "Today"}
                          </td>

                          {/* Word Count */}
                          <td className="py-3.5 px-2.5 text-center font-mono text-slate-600 whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded-md bg-slate-100 text-[11px] font-medium text-slate-700">
                              {words}w
                            </span>
                          </td>

                          {/* Marks Obtained */}
                          <td className="py-3.5 px-2.5 text-center font-black text-slate-900 whitespace-nowrap">
                            {isEvaluated ? (
                              <span className="text-emerald-700 font-extrabold text-xs">
                                {res.marksObtained ?? res.marksAwarded}
                              </span>
                            ) : (
                              <span className="text-amber-600 italic font-semibold">—</span>
                            )}
                          </td>

                          {/* Maximum Marks */}
                          <td className="py-3.5 px-2.5 text-center font-bold text-slate-600 whitespace-nowrap">
                            {maxScore}
                          </td>

                          {/* Evaluation Status */}
                          <td className="py-3.5 px-3 text-center whitespace-nowrap">
                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold ${
                                isEvaluated
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}
                            >
                              {isEvaluated ? "Evaluated" : "Pending Review"}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => setViewingProfileStudent(res)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-bold shadow-2xs transition-all"
                                title="View Student Profile & Performance History"
                              >
                                <User size={12} />
                                <span>View Profile</span>
                              </button>
                              <button
                                onClick={() => openEvaluationModal(res)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-900 hover:bg-red-800 text-white text-[11px] font-bold shadow-2xs transition-all"
                                title="Evaluate Submitted Answer Script"
                              >
                                <PenTool size={12} />
                                <span>{isEvaluated ? "Re-evaluate" : "Evaluate"}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* CREATE / EDIT MAINS INITIATIVE MODAL - SIMPLIFIED */}
      {isFormModalOpen && (
        <Modal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          title={editingInitiative ? "Edit Mains Initiative" : "Create Mains Initiative"}
          subtitle="Configure initiative details, question prompt, and optional PDF material"
          maxWidth="max-w-2xl"
        >
          <form onSubmit={(e) => handleSaveInitiative(e)} className="space-y-4">
            {/* Initiative Title */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Initiative Title *
              </label>
              <input
                type="text"
                required
                value={initiativeForm.title}
                onChange={(e) =>
                  setInitiativeForm({ ...initiativeForm, title: e.target.value })
                }
                placeholder="e.g. Daily Mains Answer Writing - 03 Nov 2026"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
              />
            </div>

            {/* Folder & Upload / Test Date Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Folder size={13} className="text-red-900" />
                  Save in Folder *
                </label>
                <select
                  required
                  value={initiativeForm.folder || "Indian Polity"}
                  onChange={(e) =>
                    setInitiativeForm({ ...initiativeForm, folder: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
                >
                  {mainsSubjectFolders.map((f) => (
                    <option key={f} value={f}>
                      📁 {f}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Calendar size={13} className="text-red-900" />
                  Upload / Test Date *
                </label>
                <input
                  type="date"
                  required
                  value={initiativeForm.date || initiativeForm.deadline || new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setInitiativeForm({
                      ...initiativeForm,
                      date: e.target.value,
                      deadline: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
                />
              </div>
            </div>

            {/* GS Paper, Target Deadline, Marks & Words */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  UPSC GS Paper *
                </label>
                <select
                  value={initiativeForm.gsPaper}
                  onChange={(e) =>
                    setInitiativeForm({ ...initiativeForm, gsPaper: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
                >
                  {gsPapers.map((p) => {
                    const shortLabel = p.split("(")[0].trim();
                    return (
                      <option key={p} value={p}>
                        {shortLabel}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Submission Deadline
                </label>
                <input
                  type="date"
                  value={initiativeForm.deadline}
                  onChange={(e) =>
                    setInitiativeForm({
                      ...initiativeForm,
                      deadline: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Marks & Word Limit
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input
                      type="number"
                      value={initiativeForm.maxMarks}
                      onChange={(e) =>
                        setInitiativeForm({
                          ...initiativeForm,
                          maxMarks: Number(e.target.value),
                        })
                      }
                      title="Max Marks"
                      placeholder="15"
                      className="w-full pl-2.5 pr-6 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 text-center focus:outline-none focus:ring-2 focus:ring-red-900/20"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-bold pointer-events-none">M</span>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      value={initiativeForm.wordLimit}
                      onChange={(e) =>
                        setInitiativeForm({
                          ...initiativeForm,
                          wordLimit: Number(e.target.value),
                        })
                      }
                      title="Word Limit"
                      placeholder="250"
                      className="w-full pl-2.5 pr-6 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 text-center focus:outline-none focus:ring-2 focus:ring-red-900/20"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-bold pointer-events-none">W</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mains Question Prompt */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Mains Question Prompt *
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setQbSearch("");
                    setQbActiveFolder(null);
                    setIsQbPickerOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-red-950 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors shadow-2xs"
                >
                  <BookOpen className="w-3.5 h-3.5 text-red-900" />
                  Select from Question Bank
                </button>
              </div>
              <textarea
                rows={4}
                required
                value={initiativeForm.question}
                onChange={(e) =>
                  setInitiativeForm({ ...initiativeForm, question: e.target.value })
                }
                placeholder="Type or paste the UPSC Mains question prompt here..."
                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-serif leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
              />
            </div>

            {/* Reference / Model Answer PDF Upload */}
            <div className="p-3 border border-dashed border-slate-300 rounded-xl bg-slate-50/70 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50 shadow-2xs flex-shrink-0">
                  <Upload size={14} className="text-red-900" /> Upload PDF
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    className="hidden"
                  />
                </label>
                <div className="truncate text-xs">
                  {initiativeForm.referencePdfName ? (
                    <span className="font-semibold text-emerald-700 flex items-center gap-1 truncate">
                      ✓ {initiativeForm.referencePdfName}
                    </span>
                  ) : (
                    <span className="text-slate-400">
                      Upload question paper or model answer PDF (optional)
                    </span>
                  )}
                </div>
              </div>
              {initiativeForm.referencePdfName && (
                <button
                  type="button"
                  onClick={() => {
                    setPdfFile(null);
                    setInitiativeForm((prev) => ({
                      ...prev,
                      referencePdfName: "",
                      referencePdfUrl: "",
                    }));
                  }}
                  className="text-xs text-rose-600 hover:underline font-bold flex-shrink-0"
                >
                  Remove
                </button>
              )}
            </div>

            {/* Actions: Save as Draft & Publish */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Status:
                </span>
                <select
                  value={initiativeForm.status}
                  onChange={(e) =>
                    setInitiativeForm({
                      ...initiativeForm,
                      status: e.target.value,
                    })
                  }
                  className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:outline-none"
                >
                  <option value="Published">Published</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSaveInitiative(e, "Draft")}
                  className="px-4 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
                >
                  Save as Draft
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSaveInitiative(e, "Published")}
                  className="px-5 py-2 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-xl shadow-md transition-all active:scale-95 cursor-pointer"
                >
                  {editingInitiative ? "Save & Publish" : "Publish Initiative"}
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* EVALUATION MODAL: INSPECT ANSWER & ENTER MARKS / FEEDBACK */}
      {evaluatingResult && (
        <Modal
          isOpen={!!evaluatingResult}
          onClose={() => setEvaluatingResult(null)}
          title={`Evaluate Mains Answer: ${evaluatingResult.studentName}`}
          subtitle={`Registration ID: ${evaluatingResult.regId} • Submitted on ${evaluatingResult.submissionDate}`}
          maxWidth="max-w-4xl"
        >
          <form onSubmit={handleSaveEvaluation} className="space-y-6">
            {/* Question Card */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <span className="text-[10px] font-bold uppercase tracking-wider text-red-900 bg-red-50 px-2 py-0.5 rounded-md">
                {evaluatingResult.initiativeName}
              </span>
              <p className="text-xs sm:text-sm font-semibold text-slate-900 mt-2 font-serif">
                "{evaluatingResult.questionText || "Evaluate the role of Parliamentary Committees in ensuring executive accountability in India. Discuss recent challenges."}"
              </p>
              <div className="mt-2 flex items-center gap-4 text-[11px] text-slate-500 font-mono">
                <span>Maximum Marks: {evaluatingResult.totalMarks || 15}</span>
                <span>Word Limit: 250 words</span>
              </div>
            </div>

            {/* Student Answer */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Student's Submitted Answer Script
                </label>
                <span className="text-[11px] text-slate-400 font-mono">
                  Word count: ~240 words
                </span>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-2xl max-h-56 overflow-y-auto text-xs text-slate-800 leading-relaxed font-serif whitespace-pre-line shadow-inner">
                {evaluatingResult.submittedAnswer ||
                  `Introduction:
Parliamentary Committees are described as the 'eyes and ears' of the legislature. Under Rules 287-307, the Departmentally Related Standing Committees (DRSCs) scrutinize budgetary demands and legislative bills in an in-camera, non-partisan atmosphere.

Key Contributions:
1. Substantive Scrutiny: Provides detailed clause-by-clause examination of technical legislation that the plenary House lacks time for.
2. Cross-Party Consensus: Members deliberate away from televised camera pressure, fostering bipartisan consensus.
3. Financial Vigilance: The Public Accounts Committee (PAC) and Estimates Committee ensure public funds are utilized as sanctioned.

Recent Challenges:
- Declining referral of bills: In the 17th Lok Sabha, only ~16% of bills were referred to committees, down from 71% in the 15th Lok Sabha.
- Bypassing through Money Bill classification.
- Low attendance and lack of dedicated research support staff.

Way Forward:
- Make referral of major bills to standing committees mandatory by amending the Rules of Procedure.
- Establish a Parliamentary Budget Office (PBO) to equip committees with independent economic analysis.`}
              </div>
            </div>

            {/* Evaluation Form inputs: Marks & Feedback */}
            <div className="p-5 bg-gradient-to-r from-red-50 to-amber-50/50 rounded-2xl border border-red-200/80 space-y-4">
              <h4 className="text-xs font-bold text-red-950 uppercase tracking-wider flex items-center gap-1.5">
                <Award size={15} className="text-red-900" /> Faculty Grading & Detailed Feedback
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Marks Awarded (Max: {evaluatingResult.totalMarks || 15}) *
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max={evaluatingResult.totalMarks || 15}
                    required
                    value={evalData.marksObtained}
                    onChange={(e) =>
                      setEvalData({
                        ...evalData,
                        marksObtained: Number(e.target.value),
                      })
                    }
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-base font-extrabold text-red-900 focus:outline-none focus:ring-2 focus:ring-red-900"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Calculated Percentage
                  </label>
                  <div className="px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-base font-bold text-slate-700">
                    {Math.round(
                      (evalData.marksObtained /
                        (evaluatingResult.totalMarks || 15)) *
                        100
                    )}
                    %
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Result Status
                  </label>
                  <select
                    value={evalData.evaluationStatus}
                    onChange={(e) =>
                      setEvalData({
                        ...evalData,
                        evaluationStatus: e.target.value,
                      })
                    }
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                  >
                    <option value="Evaluated">Evaluated & Verified</option>
                    <option value="Needs Revision">Needs Student Revision</option>
                  </select>
                </div>
              </div>

              {/* Feedback */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Evaluator Remarks, Strengths & Areas of Improvement *
                </label>
                <textarea
                  rows={3}
                  required
                  value={evalData.feedback}
                  onChange={(e) =>
                    setEvalData({ ...evalData, feedback: e.target.value })
                  }
                  placeholder="Detail suggestions regarding introduction, sub-headings, flowchart use, and concluding way forward..."
                  className="w-full px-3.5 py-2 bg-white border border-slate-300 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="pt-2 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setEvaluatingResult(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 px-6 py-2.5 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-xl shadow-lg shadow-red-900/20 transition-all transform active:scale-95"
              >
                <Check size={15} /> Save & Publish Marks to Student
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <Modal
          isOpen={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
          title="Delete Mains Initiative"
          maxWidth="max-w-md"
          footer={
            <div className="w-full flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteMainsInitiative(deleteConfirmId);
                  setDeleteConfirmId(null);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-700 hover:bg-rose-800 rounded-xl shadow"
              >
                Delete
              </button>
            </div>
          }
        >
          <p className="text-xs text-slate-600 leading-relaxed">
            Are you sure you want to delete this Mains initiative? This will remove the prompt and any un-evaluated answers.
          </p>
        </Modal>
      )}

      {/* Question Bank Picker Modal (Folder-Based) */}
      {isQbPickerOpen && (
        <Modal
          isOpen={isQbPickerOpen}
          onClose={() => {
            setIsQbPickerOpen(false);
            setQbActiveFolder(null);
          }}
          title="Browse Mains Question Bank Folders"
          subtitle="Browse GS Paper folders to select questions. Auto-populates question prompt, marks and word limit."
          maxWidth="max-w-3xl"
        >
          <div className="space-y-4">
            {/* If NO folder selected: Show GS Paper sections & Subject Folders */}
            {!qbActiveFolder ? (
              <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                {Object.entries(mainsStructure).map(([paper, subjects]) => (
                  <div key={paper} className="space-y-2">
                    <div className="flex items-center gap-2 border-b border-slate-100 pb-1">
                      <span className="w-2 h-2 rounded-full bg-red-900" />
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                        {paper}
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {subjects.map((subj) => {
                        const count = (mainsQuestionBank || []).filter(
                          (q) =>
                            (q?.gsPaper || "").trim().toLowerCase() === paper.toLowerCase() &&
                            (q?.subject || "").trim().toLowerCase() === subj.toLowerCase()
                        ).length;

                        return (
                          <div
                            key={subj}
                            onClick={() => {
                              setQbActiveFolder({ gsPaper: paper, subject: subj });
                              setQbSearch("");
                            }}
                            className="p-3 bg-slate-50 hover:bg-red-50/50 border border-slate-200 hover:border-red-900/40 rounded-xl transition-all cursor-pointer group flex flex-col justify-between"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Folder className="w-5 h-5 text-red-900 group-hover:scale-110 transition-transform" />
                              <span className="text-[10px] font-bold text-slate-600 bg-white border border-slate-200 px-1.5 py-0.5 rounded-full">
                                {count}
                              </span>
                            </div>
                            <p className="font-bold text-slate-800 group-hover:text-red-950 text-xs truncate">
                              {subj}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* If Folder IS selected: Show Questions in that folder */
              <div className="space-y-3">
                {/* Breadcrumbs & Search */}
                <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-100">
                  <button
                    type="button"
                    onClick={() => setQbActiveFolder(null)}
                    className="flex items-center gap-1 text-xs font-bold text-red-900 hover:underline"
                  >
                    <ArrowLeft size={13} /> Back to Folders
                  </button>
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-slate-500">Folder:</span>
                    <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded flex items-center gap-1">
                      <FolderOpen size={12} className="text-red-900" />
                      {qbActiveFolder.gsPaper} • {qbActiveFolder.subject}
                    </span>
                  </div>
                </div>

                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    value={qbSearch}
                    onChange={(e) => setQbSearch(e.target.value)}
                    placeholder={`Search within ${qbActiveFolder.subject}...`}
                    className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                  />
                </div>

                {/* Questions List */}
                <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 pr-1 space-y-2">
                  {(mainsQuestionBank || [])
                    .filter((q) => {
                      const matchesPaper =
                        (q?.gsPaper || "").trim().toLowerCase() ===
                        qbActiveFolder.gsPaper.toLowerCase();
                      const matchesSubject =
                        (q?.subject || "").trim().toLowerCase() ===
                        qbActiveFolder.subject.toLowerCase();
                      const matchesSearch =
                        !qbSearch.trim() ||
                        (q?.questionText || "").toLowerCase().includes(qbSearch.toLowerCase()) ||
                        (q?.topic || "").toLowerCase().includes(qbSearch.toLowerCase());
                      return matchesPaper && matchesSubject && matchesSearch;
                    })
                    .map((q) => (
                      <div
                        key={q.id}
                        className="p-3 border border-slate-200/80 hover:border-red-900/40 hover:bg-red-50/20 rounded-xl transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 cursor-pointer group"
                        onClick={() => {
                          handleSelectFromQb(q);
                          setQbActiveFolder(null);
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-serif text-xs text-slate-900 leading-relaxed line-clamp-3 group-hover:text-red-950 transition-colors">
                            {q.questionText}
                          </p>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500">
                            {q.topic && (
                              <>
                                <span className="font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                                  {q.topic}
                                </span>
                                <span>•</span>
                              </>
                            )}
                            <span>Max Marks: <strong className="text-slate-700">{q.maxMarks}</strong></span>
                            <span>•</span>
                            <span>Word Limit: <strong className="text-slate-700">{q.wordLimit} words</strong></span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectFromQb(q);
                            setQbActiveFolder(null);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-lg shadow-xs transition-colors flex-shrink-0"
                        >
                          <Check size={13} /> Select
                        </button>
                      </div>
                    ))}

                  {(mainsQuestionBank || []).filter(
                    (q) =>
                      (q?.gsPaper || "").trim().toLowerCase() ===
                        qbActiveFolder.gsPaper.toLowerCase() &&
                      (q?.subject || "").trim().toLowerCase() ===
                        qbActiveFolder.subject.toLowerCase()
                  ).length === 0 && (
                    <div className="py-8 text-center text-xs text-slate-400">
                      No Mains questions found in this folder.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* STUDENT PROFILE DRAWER */}
      <StudentProfileDrawer
        isOpen={!!viewingProfileStudent}
        onClose={() => setViewingProfileStudent(null)}
        student={viewingProfileStudent}
        mode="mains"
        onInspectMainsSubmission={(sub) => {
          setViewingProfileStudent(null);
          openEvaluationModal(sub);
        }}
      />
    </div>
  );
};

export default MainsManagement;
